import { PhotoStatusOverlay } from './photo_status_overlay.js';
import { VirtualPhotoGrid } from './virtual_photo_grid.js';
import { VirtualPhotoStrip } from './virtual_photo_strip.js';

// UI Rendering Manager
export class UIManager {
  constructor(stateManager, photoManager) {
    this.stateManager = stateManager;
    this.photoManager = photoManager;
    this.sliderManager = null; // Will be set by EditorApp
    this.viewManager = null; // Will be set by EditorApp
    this.editorState = stateManager.getEditorState();
    this.folderStateManager = stateManager.getFolderStateManager();
    
    // Virtual scrolling instances
    this.virtualPhotoGrid = null;
    this.virtualPhotoStrip = null;
    
    // Subscribe to reactive state changes
    this.setupStateListeners();
    this.setupFolderStateManagerListeners();
    this.initializeVirtualScrollers();
  }
  
  /**
   * Initialize virtual scrolling for photo grid and strip
   */
  initializeVirtualScrollers() {
    // Photo grid (Photos page)
    const photoGrid = document.getElementById('photo-grid');
    const photoGridScroller = photoGrid?.parentElement; // The scrollable container
    
    if (photoGrid && photoGridScroller) {
      this.virtualPhotoGrid = new VirtualPhotoGrid(photoGrid, {
        scrollContainer: photoGridScroller,
        itemHeight: 128, // h-28 + padding + ring
        itemGap: 16, // gap-4
        overscanRows: 3,
        viewportPhotoLoader: this.stateManager.viewportPhotoLoader,
        photoService: this.stateManager.photoService,
        folderStateManager: this.folderStateManager,
        editorState: this.editorState
      });
    }
    
    // Photo strip (Edit page)
    const photoSelector = document.getElementById('photo-selector');
    const photoSelectorBar = document.getElementById('photo-selector-bar');
    
    if (photoSelector && photoSelectorBar) {
      this.virtualPhotoStrip = new VirtualPhotoStrip(photoSelector, {
        scrollContainer: photoSelectorBar,
        itemWidth: 96, // w-20 + padding + ring
        itemGap: 8, // gap-2
        overscanItems: 10,
        viewportPhotoLoader: this.stateManager.viewportPhotoLoader,
        photoService: this.stateManager.photoService,
        folderStateManager: this.folderStateManager,
        editorState: this.editorState
      });
    }
  }
  
  /**
   * Setup FolderStateManager listeners for new/removed folders
   */
  setupFolderStateManagerListeners() {
    // When a new FolderState is created, attach listeners
    this.folderStateManager.addEventListener('folderStateCreated', (e) => {
      const { folderState } = e.detail;
      this.attachFolderStateListeners(folderState);
      this.renderFolderList(); // Re-render folder list
    });
    
    // When a FolderState is removed, re-render folder list
    this.folderStateManager.addEventListener('folderStateRemoved', (e) => {
      this.renderFolderList(); // Re-render folder list
    });
  }
  
  /**
   * Attach event listeners to a FolderState
   */
  attachFolderStateListeners(folderState) {
    // When folder is updated (e.g., photo count changed), re-render folder list
    folderState.addEventListener('folderUpdated', () => {
      this.renderFolderList();
    });
    
    // When upload progress changes, re-render folder list
    folderState.addEventListener('uploadProgress', () => {
      this.renderFolderList();
    });
    
    // When photo is added, update photo grid if this is the current folder
    folderState.addEventListener('photoAdded', (e) => {
      const state = this.editorState.get();
      if (state.currentFolderId === folderState.getId()) {
        // Photo grid will be updated by FolderManager
      }
    });
    
    // When photo status updates, update folder list to show processing counts
    // Note: Virtual components now handle their own photo updates via direct event listeners
    folderState.addEventListener('photoUpdated', (e) => {
      // Re-render folder list to update processing counts
      this.renderFolderList();
    });
  }
  
  /**
   * Setup reactive state listeners
   */
  setupStateListeners() {
    // When currentFolderId changes, re-render folder list to update highlighting
    this.editorState.addEventListener('currentFolderId', () => {
      this.renderFolderList();
    });
    
    // Listen for selection changes to update virtual scrollers
    this.editorState.addEventListener('selectedPhotoIds', (e) => {
      const state = this.editorState.get();
      const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
      const folderState = state.currentFolderId ? this.folderStateManager.getFolderState(state.currentFolderId) : null;
      const photoMetadata = folderState?.getPhotoMetadata() || [];
      
      // Update photo grid if on Photos page
      if (state.currentPage === 'photos') {
        // Use photo metadata for photo grid (only needs thumbnails, not full data)
        this.renderPhotos(photoMetadata, state.selectedPhotoIds, activePhoto);
      }
      
      // Update filmstrip if on Edit page
      if (state.currentPage === 'edit') {
        // Use photo metadata for filmstrip (only needs thumbnails)
        this.renderPhotoSelector(photoMetadata, activePhoto, state.selectedPhotoIds, activePhoto);
      }
    });
    
    // Listen for active photo changes
    this.editorState.addEventListener('activePhotoId', (e) => {
      const state = this.editorState.get();
      const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
      const folderState = state.currentFolderId ? this.folderStateManager.getFolderState(state.currentFolderId) : null;
      const photoMetadata = folderState?.getPhotoMetadata() || [];
      
      // Update photo grid if on Photos page
      if (state.currentPage === 'photos') {
        // Use photo metadata for photo grid (only needs thumbnails, not full data)
        this.renderPhotos(photoMetadata, state.selectedPhotoIds, activePhoto);
      }
      
      // Update filmstrip if on Edit page (but photoChanged event will handle main view)
      if (state.currentPage === 'edit') {
        // Use photo metadata for filmstrip (only needs thumbnails)
        this.renderPhotoSelector(photoMetadata, activePhoto, state.selectedPhotoIds, activePhoto);
      }
    });
    
    // When photo changes, update Edit page if we're on it
    this.editorState.addEventListener('photoChanged', (e) => {
      const { photoId, photo } = e.detail;
      // Get current state directly from editorState (which is always up-to-date)
      const state = this.editorState.get();
      
      // Only update if we're on Edit page and have a photo
      if (state.currentPage === 'edit' && photoId && photo) {
        this.displayEditPhoto(photo);
        
        // Also update filmstrip with current selection state
        const folderState = state.currentFolderId ? this.folderStateManager.getFolderState(state.currentFolderId) : null;
        const photoMetadata = folderState?.getPhotoMetadata() || [];
        if (photoMetadata.length > 0) {
          this.renderPhotoSelector(photoMetadata, photo, state.selectedPhotoIds, photo);
        }
      }
    });
    
    // When page changes, update display
    this.editorState.addEventListener('currentPage', (e) => {
      const newPage = e.detail.value;
      
      // Hide all pages
      document.querySelectorAll('.page-content').forEach(p => p.classList.add('hidden'));
      
      // Show selected page
      document.getElementById(`${newPage}-page`)?.classList.remove('hidden');
      
      // Update tab styling (desktop)
      document.querySelectorAll('.tab-btn').forEach(btn => {
        if (btn.dataset.page === newPage) {
          btn.classList.add('border-blue-500', 'text-white', 'bg-gray-700');
          btn.classList.remove('border-transparent', 'text-gray-400');
        } else {
          btn.classList.remove('border-blue-500', 'text-white', 'bg-gray-700');
          btn.classList.add('border-transparent', 'text-gray-400');
        }
      });
      
      // If Edit page, display the active photo
      if (newPage === 'edit') {
        const state = this.editorState.get();
        const photos = this.photoManager.getPhotos();
        
        if (state.activePhotoId && photos.length > 0) {
          const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
          if (activePhoto) {
            this.displayEditPhoto(activePhoto);
            // Render filmstrip with metadata
            const folderState = state.currentFolderId ? this.folderStateManager.getFolderState(state.currentFolderId) : null;
            const photoMetadata = folderState?.getPhotoMetadata() || [];
            this.renderPhotoSelector(photoMetadata, activePhoto, state.selectedPhotoIds, activePhoto);
          }
        }
      }
      
      // If Export page, render export page
      if (newPage === 'export') {
        this.renderExportPage();
        this.updateFilenamePatternPreview();
        // Update the "Save with Original Files" option visibility
        if (this.app?.updateSaveWithOriginalFilesOption) {
          this.app.updateSaveWithOriginalFilesOption();
        }
      }
    });
  }

  /**
   * Show a specific page
   */
  showPage(page) {
    const state = this.editorState.get();
    const previousPage = state.currentPage;
    
    // Clean up preview bar when leaving Edit page
    if (previousPage === 'edit' && page !== 'edit' && this.sliderManager) {
      this.sliderManager.destroyPreviewBar();
    }
    
    // Update state (using reactive state)
    // Note: If state doesn't change, the listener won't fire, so we need to update DOM here too
    const stateChanged = previousPage !== page;
    if (stateChanged) {
      this.editorState.set({ currentPage: page });
    }
    
    // Always update DOM to ensure it's in sync (state listener only fires on actual changes)
    // Hide all pages
    document.querySelectorAll('.page-content').forEach(p => p.classList.add('hidden'));
    
    // Show selected page
    document.getElementById(`${page}-page`)?.classList.remove('hidden');
    
    // Update tab styling (desktop)
    document.querySelectorAll('.tab-btn').forEach(btn => {
      if (btn.dataset.page === page) {
        btn.classList.add('border-blue-500', 'text-white', 'bg-gray-700');
        btn.classList.remove('border-transparent', 'text-gray-400');
      } else {
        btn.classList.remove('border-blue-500', 'text-white', 'bg-gray-700');
        btn.classList.add('border-transparent', 'text-gray-400');
      }
    });

    // If Edit page, display the active photo and filmstrip
    if (page === 'edit') {
      const photos = this.photoManager.getPhotos();
      let activePhotoId = state.activePhotoId;
      
      // If no active photo but we have photos and a folder, set the first photo as active
      if (!activePhotoId && photos.length > 0 && state.currentFolderId) {
        const firstPhoto = photos[0];
        this.editorState.setActivePhoto(firstPhoto.id, firstPhoto);
        activePhotoId = firstPhoto.id;
      }
      
      if (activePhotoId && photos.length > 0) {
        const activePhoto = this.photoManager.getPhotoById(activePhotoId);
        
        if (activePhoto) {
          // Display the main edit photo
          this.displayEditPhoto(activePhoto);
          
          // Render the photo selector filmstrip with metadata
          const folderState = state.currentFolderId ? this.folderStateManager.getFolderState(state.currentFolderId) : null;
          const photoMetadata = folderState?.getPhotoMetadata() || [];
          this.renderPhotoSelector(photoMetadata, activePhoto, state.selectedPhotoIds, activePhoto);
        }
      }
    }
    
    // If Photos page, sync active photo and selection from Edit page (only when transitioning)
    if (page === 'photos' && previousPage === 'edit') {
      const photos = this.photoManager.getPhotos();
      const activePhotoId = state.activePhotoId;
      
      // If we have an activePhotoId from Edit page, make sure it's in selection
      if (activePhotoId && photos.length > 0) {
        // Selection is already managed by reactive state
      }
      
      // Re-render photo grid to show the selection
      const folderState = state.currentFolderId ? this.folderStateManager.getFolderState(state.currentFolderId) : null;
      const photoMetadata = folderState?.getPhotoMetadata() || [];
      if (photoMetadata.length > 0) {
        const activePhoto = this.photoManager.getPhotoById(state.activePhotoId);
        this.renderPhotos(photoMetadata, state.selectedPhotoIds, activePhoto);
      }
    }
  }

  /**
   * Render folder list from FolderStateManager
   */
  renderFolderList() {
    const folders = this.folderStateManager.getAllFolders();
    const state = this.editorState.get();
    const currentFolderId = state.currentFolderId;
    
    const folderList = document.getElementById('folder-list');
    if (!folderList) return;
    
    folderList.innerHTML = '';

    if (folders.length === 0) {
      folderList.innerHTML = `
        <div class="text-center py-8 px-4">
          <svg class="w-12 h-12 text-gray-600 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
          </svg>
          <p class="text-gray-500 text-sm">No folders yet</p>
          <p class="text-gray-600 text-xs mt-1">Click + to add a folder</p>
        </div>
      `;
      return;
    }

    folders.forEach(folder => {
      const folderState = this.folderStateManager.getFolderState(folder.id);
      const uploadProgress = folderState?.getUploadProgress();
      const hasUploadProgress = uploadProgress && uploadProgress.total > 0;
      const isActive = currentFolderId === folder.id;
      const photoCount = folder.photos_count || folderState?.getPhotoCount() || 0;
      
      const div = document.createElement('div');
      div.className = `folder-item p-3 rounded-lg cursor-pointer transition-all duration-200 ${
        isActive 
          ? 'bg-blue-600/90 shadow-lg ring-1 ring-blue-400/30' 
          : 'hover:bg-gray-750 hover:shadow-md'
      }`;
      
      // Calculate processing status
      const processingStats = PhotoStatusOverlay.getFolderProcessingStats(folder.photos || []);
      const processingProgressHTML = PhotoStatusOverlay.createFolderProgressHTML(processingStats, isActive);
      
      // Determine folder icon color based on state
      let folderIconColor = 'text-blue-400';
      if (isActive) {
        // When active, prioritize active state styling
        if (hasUploadProgress) {
          folderIconColor = 'text-blue-200';
        } else if (processingStats.hasProcessing) {
          folderIconColor = 'text-green-300';
        } else {
          folderIconColor = 'text-white';
        }
      } else {
        // When inactive, show status colors
        if (hasUploadProgress) {
          folderIconColor = 'text-blue-500';
        } else if (processingStats.hasProcessing) {
          folderIconColor = 'text-green-400';
        }
      }
      
      div.innerHTML = `
        <div class="flex items-center justify-between mb-0.5">
          <div class="flex items-center space-x-3 min-w-0 flex-1">
            <svg class="w-5 h-5 ${folderIconColor} flex-shrink-0 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"></path>
            </svg>
            <span class="truncate ${isActive ? 'text-white font-semibold' : 'text-gray-100 font-medium'}">${folder.name}</span>
          </div>
          <span class="${
            isActive 
              ? 'bg-white/20 text-white px-2 py-0.5 rounded-full text-xs font-semibold' 
              : 'bg-gray-700 text-gray-300 px-2 py-0.5 rounded-full text-xs font-medium'
          } ml-2 flex-shrink-0">${photoCount}</span>
        </div>
        ${hasUploadProgress ? `
          <div class="mt-2.5 pt-2 border-t ${isActive ? 'border-white/20' : 'border-gray-700'} space-y-2">
            ${uploadProgress.currentSize && uploadProgress.currentSize.name ? `
              <!-- Current Size Progress -->
              <div>
                <div class="flex items-center justify-between text-xs mb-1">
                  <div class="flex items-center space-x-1.5">
                    <svg class="w-3.5 h-3.5 ${isActive ? 'text-blue-200' : 'text-blue-400'} animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                    </svg>
                    <span class="${isActive ? 'text-blue-100' : 'text-gray-400'}">Uploading ${uploadProgress.currentSize.name}...</span>
                  </div>
                  <span class="${isActive ? 'text-blue-100 font-medium' : 'text-gray-400'}">${uploadProgress.currentSize.uploaded}/${uploadProgress.currentSize.total}</span>
                </div>
                <div class="w-full ${isActive ? 'bg-white/20' : 'bg-gray-700'} rounded-full h-1.5 overflow-hidden shadow-inner">
                  <div class="${isActive ? 'bg-blue-300' : 'bg-blue-400'} h-1.5 rounded-full transition-all duration-300 shadow-sm" style="width: ${uploadProgress.currentSize.percentage}%"></div>
                </div>
              </div>
            ` : ''}
            
            <!-- Total Progress -->
            <div>
              <div class="flex items-center justify-between text-xs mb-1">
                <span class="${isActive ? 'text-blue-50/80' : 'text-gray-500'} font-medium">Total progress</span>
                <span class="${isActive ? 'text-blue-100 font-semibold' : 'text-gray-400'}">${uploadProgress.uploaded}/${uploadProgress.total}</span>
              </div>
              <div class="w-full ${isActive ? 'bg-white/20' : 'bg-gray-700'} rounded-full h-2 overflow-hidden shadow-inner">
                <div class="${isActive ? 'bg-white' : 'bg-blue-500'} h-2 rounded-full transition-all duration-300 shadow-sm" style="width: ${uploadProgress.percentage}%"></div>
              </div>
            </div>
          </div>
        ` : ''}
        ${processingProgressHTML}
      `;
      
      div.dataset.folderId = folder.id;
      folderList.appendChild(div);
    });
  }
  
  /**
   * Render folders in sidebar (for backwards compatibility)
   * Now just calls renderFolderList()
   */
  renderFolders(folders, currentFolder, uploadProgress = {}) {
    this.renderFolderList();
  }

  /**
   * Render photos in grid (using virtual scrolling)
   */
  renderPhotos(photos, selectedPhotos, activePhoto) {
    const photoGrid = document.getElementById('photo-grid');
    const emptyState = document.getElementById('empty-state');

    if (!photoGrid || !emptyState) return;

    if (photos.length === 0) {
      if (this.virtualPhotoGrid) {
        this.virtualPhotoGrid.update([], new Set(), null, null);
      }
      emptyState.classList.remove('hidden');
      return;
    }

    emptyState.classList.add('hidden');
    
    // Use virtual scrolling if available
    if (this.virtualPhotoGrid) {
      const state = this.editorState.get();
      this.virtualPhotoGrid.update(photos, selectedPhotos, activePhoto, state.currentFolderId);
    } else {
      // Fallback to non-virtual rendering (shouldn't happen, but just in case)
      console.warn('Virtual photo grid not initialized, falling back to non-virtual rendering');
      this.renderPhotosNonVirtual(photos, selectedPhotos, activePhoto);
    }
  }
  
  /**
   * Fallback non-virtual rendering (for when virtual scroller isn't available)
   */
  renderPhotosNonVirtual(photos, selectedPhotos, activePhoto) {
    const photoGrid = document.getElementById('photo-grid');
    if (!photoGrid) return;
    
    photoGrid.innerHTML = '';

    photos.forEach(photo => {
      const thumbUrl = photo.thumb_256_url || photo.thumb_1024_url;
      
      if (!thumbUrl) {
        console.warn('No image URL for photo:', photo);
        return;
      }

      const isSelected = selectedPhotos.has(photo.id);
      const isActive = activePhoto?.id === photo.id;
      
      // Outer wrapper with consistent padding to accommodate rings
      const wrapper = document.createElement('div');
      wrapper.className = 'p-1';
      
      const div = document.createElement('div');
      // Use ring-4 styling to match preview carousel (doesn't affect layout)
      if (isActive) {
        div.className = 'relative cursor-pointer rounded ring-4 ring-white shadow-xl';
      } else if (isSelected) {
        div.className = 'relative cursor-pointer rounded ring-4 ring-blue-400 shadow-md';
      } else {
        div.className = 'relative cursor-pointer rounded hover:ring-2 hover:ring-gray-500';
      }
      
      // Container to maintain square aspect ratio
      const imgContainer = document.createElement('div');
      imgContainer.className = 'w-full h-28 flex items-center justify-center bg-gray-800 rounded';
      
      const img = document.createElement('img');
      img.src = thumbUrl;
      img.alt = photo.original_filename;
      img.className = 'max-w-full max-h-full object-contain';
      img.loading = 'lazy';
      
      imgContainer.appendChild(img);
      div.appendChild(imgContainer);
      
      // Add processing status overlay if needed
      const statusOverlay = PhotoStatusOverlay.createOverlayHTML(photo.processing_status);
      if (statusOverlay) {
        imgContainer.insertAdjacentHTML('beforeend', statusOverlay);
      }
      
      // Show checkmark for selected (but not active) photos
      if (isSelected && !isActive) {
        const checkmark = document.createElement('div');
        checkmark.className = 'absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center z-10';
        checkmark.innerHTML = '<svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"></path></svg>';
        div.appendChild(checkmark);
      }
      
      // Show special indicator for active photo
      if (isActive) {
        const activeIndicator = document.createElement('div');
        activeIndicator.className = 'absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center z-10 shadow-lg';
        activeIndicator.innerHTML = '<svg class="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16z"></path></svg>';
        div.appendChild(activeIndicator);
      }
      
      div.dataset.photoId = photo.id;
      wrapper.appendChild(div);
      photoGrid.appendChild(wrapper);
    });
  }

  /**
   * Render photo selector (filmstrip on Edit page, using virtual scrolling)
   */
  renderPhotoSelector(photos, currentPhoto, selectedPhotos, activePhoto, smoothScroll = false) {
    const selector = document.getElementById('photo-selector');
    const selectorBar = document.getElementById('photo-selector-bar');
    
    if (!selector || !selectorBar) return;
    
    // Use virtual scrolling if available
    if (this.virtualPhotoStrip) {
      const state = this.editorState.get();
      this.virtualPhotoStrip.update(photos, currentPhoto, selectedPhotos, activePhoto, smoothScroll, state.currentFolderId);
    } else {
      // Fallback to non-virtual rendering (shouldn't happen, but just in case)
      console.warn('Virtual photo strip not initialized, falling back to non-virtual rendering');
      this.renderPhotoSelectorNonVirtual(photos, currentPhoto, selectedPhotos, activePhoto, smoothScroll);
    }
  }
  
  /**
   * Fallback non-virtual rendering for photo selector
   */
  renderPhotoSelectorNonVirtual(photos, currentPhoto, selectedPhotos, activePhoto, smoothScroll = false) {
    const selector = document.getElementById('photo-selector');
    const selectorBar = document.getElementById('photo-selector-bar');
    
    if (!selector || !selectorBar) return;
    
    selector.innerHTML = '';
    let currentElement = null;

    photos.forEach(photo => {
      const thumbUrl = photo.thumb_256_url || photo.thumb_1024_url;
      if (!thumbUrl) return;

      const isCurrent = currentPhoto?.id === photo.id;
      const isSelected = selectedPhotos.has(photo.id);
      const isActive = activePhoto?.id === photo.id;
      
      // Wrapper to prevent layout shift (padding accommodates borders/scaling)
      const wrapper = document.createElement('div');
      wrapper.className = 'flex-shrink-0 p-1';
      
      const div = document.createElement('div');
      div.className = 'relative cursor-pointer transition-all rounded';
      
      // Apply styling based on state - make selections VERY obvious
      if (isCurrent) {
        div.className += ' ring-4 ring-white scale-110 shadow-xl';
      } else if (isActive) {
        div.className += ' ring-4 ring-blue-500 scale-105 shadow-lg hover:shadow-xl';
      } else if (isSelected) {
        div.className += ' ring-4 ring-blue-400 shadow-md hover:shadow-lg';
      } else {
        div.className += ' opacity-60 hover:opacity-100 hover:ring-2 hover:ring-gray-500';
      }
      
      div.innerHTML = `<img src="${thumbUrl}" alt="${photo.original_filename}" class="w-20 h-20 object-cover rounded" loading="lazy">`;
      
      // Add processing status overlay if needed
      const statusOverlay = PhotoStatusOverlay.createOverlayHTML(photo.processing_status);
      if (statusOverlay) {
        div.insertAdjacentHTML('beforeend', statusOverlay);
      }
      
      // Add selection indicators
      if (isCurrent) {
        // Current photo (being edited) - white badge with blue dot
        const currentIndicator = document.createElement('div');
        currentIndicator.className = 'absolute -top-0.5 -right-0.5 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-lg border-2 border-gray-800 z-10';
        currentIndicator.innerHTML = '<svg class="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16z"></path></svg>';
        div.appendChild(currentIndicator);
      } else if (isActive) {
        // Active photo - blue badge with white dot
        const activeIndicator = document.createElement('div');
        activeIndicator.className = 'absolute top-0.5 right-0.5 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center shadow-md border border-white z-10';
        activeIndicator.innerHTML = '<svg class="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a8 8 0 100 16 8 8 0 000-16z"></path></svg>';
        div.appendChild(activeIndicator);
      } else if (isSelected) {
        // Selected photo - blue badge with white checkmark
        const checkmark = document.createElement('div');
        checkmark.className = 'absolute -top-0.5 -right-0.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10';
        checkmark.innerHTML = '<svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"></path></svg>';
        div.appendChild(checkmark);
      }
      
      div.dataset.photoId = photo.id;
      wrapper.appendChild(div);
      selector.appendChild(wrapper);

      if (isCurrent) {
        currentElement = wrapper;
      }
    });

    // Scroll to center the current photo
    if (currentElement && selectorBar) {
      setTimeout(() => {
        const containerWidth = selectorBar.offsetWidth;
        const elementLeft = currentElement.offsetLeft;
        const elementWidth = currentElement.offsetWidth;
        const scrollPosition = elementLeft - (containerWidth / 2) + (elementWidth / 2);
        
        selectorBar.scrollTo({
          left: scrollPosition,
          behavior: smoothScroll ? 'smooth' : 'auto'
        });
      }, 0);
    }
  }

  /**
   * Display edit photo
   */
  displayEditPhoto(photo) {
    const editImage = document.getElementById('edit-image');
    const emptyState = document.getElementById('edit-empty-state');
    
    if (!editImage || !emptyState) return;
    
    // Update view manager images (it will handle all view updates including edit-image)
    // Pass the cached rendered URL if available so we don't lose the render
    if (this.viewManager) {
      this.viewManager.updateViewImages(photo, this.viewManager.currentRenderedImageUrl);
    }
    
    // Render sliders for this photo
    if (this.sliderManager) {
      this.sliderManager.renderSliders(photo);
    }
  }
  
  /**
   * Set view manager reference
   */
  setViewManager(viewManager) {
    this.viewManager = viewManager;
  }

  /**
   * Render Export page
   */
  renderExportPage() {
    console.log('[UIManager] >>> renderExportPage called');
    const state = this.editorState.get();
    const queueItems = state.exportQueueItems || [];
    console.log(`[UIManager] Queue items count: ${queueItems.length}`);
    
    // Update queue count
    const queueCount = document.getElementById('export-queue-count');
    if (queueCount) {
      queueCount.textContent = queueItems.length;
    }
    
    // Update export badges on tabs
    this.updateExportBadges(queueItems.length);
    
    // Show/hide empty state
    const emptyState = document.getElementById('export-queue-empty');
    const queueList = document.getElementById('export-queue-list');
    
    if (queueItems.length === 0) {
      emptyState?.classList.remove('hidden');
      queueList?.classList.add('hidden');
    } else {
      emptyState?.classList.add('hidden');
      queueList?.classList.remove('hidden');
    }
    
    // Render queue items - use requestAnimationFrame to avoid blocking
    if (queueList) {
      // Only re-render if we're not currently rendering to avoid cascading updates
      if (this._isRenderingQueue) {
        console.log('[UIManager] ⚠️ SKIPPING queue re-render - already rendering');
        // Don't return - still need to update buttons and progress below
      } else {
        console.log('[UIManager] Starting queue render in requestAnimationFrame');
        this._isRenderingQueue = true;
        requestAnimationFrame(() => {
          try {
            console.log('[UIManager] Clearing and rebuilding queue DOM...');
            queueList.innerHTML = '';
            queueItems.forEach((item, index) => {
              console.log(`[UIManager] Creating element for item ${item.id} (${index + 1}/${queueItems.length})`);
              queueList.appendChild(this.createQueueItemElement(item));
            });
            console.log('[UIManager] Queue DOM rebuild complete');
          } finally {
            this._isRenderingQueue = false;
            console.log('[UIManager] <<< Queue render complete');
          }
        });
      }
    }
    
    // Update clear completed button visibility (always run)
    const clearCompletedBtn = document.getElementById('clear-completed-btn');
    const hasCompleted = queueItems.some(item => item.status === 'completed');
    if (clearCompletedBtn) {
      if (hasCompleted) {
        clearCompletedBtn.classList.remove('hidden');
      } else {
        clearCompletedBtn.classList.add('hidden');
      }
    }
    
    // Update start export button state (always run)
    const startExportBtn = document.getElementById('start-export-btn');
    const hasPending = queueItems.some(item => item.status === 'pending');
    if (startExportBtn) {
      startExportBtn.disabled = !hasPending || state.isExporting;
    }
    
    // Update warning for download all with 200+ items (always run)
    const downloadAllWarning = document.getElementById('download-all-warning');
    if (downloadAllWarning && queueItems.length >= 200) {
      downloadAllWarning.classList.remove('hidden');
    } else if (downloadAllWarning) {
      downloadAllWarning.classList.add('hidden');
    }
    
    // Update export progress section (always run to show/hide correctly)
    this.updateExportProgress();
    
    console.log('[UIManager] <<< renderExportPage complete (buttons updated)');
  }

  /**
   * Update export badges on tabs
   */
  updateExportBadges(count) {
    const desktopBadge = document.getElementById('desktop-export-badge');
    const mobileBadge = document.getElementById('mobile-export-badge');
    
    if (desktopBadge) {
      if (count > 0) {
        desktopBadge.textContent = count;
        desktopBadge.classList.remove('hidden');
      } else {
        desktopBadge.classList.add('hidden');
      }
    }
    
    if (mobileBadge) {
      if (count > 0) {
        mobileBadge.textContent = count;
        mobileBadge.classList.remove('hidden');
      } else {
        mobileBadge.classList.add('hidden');
      }
    }
  }

  /**
   * Update export button state based on whether photo is already in queue
   */
  updateExportButtonState() {
    const state = this.editorState.get();
    const exportBtn = document.getElementById('export-btn');
    const exportSelectedBtn = document.getElementById('export-selected-btn');
    
    if (exportBtn) {
      const activePhotoId = state.activePhotoId;
      const isInQueue = activePhotoId && this.isPhotoInQueue(activePhotoId);
      
      if (isInQueue) {
        exportBtn.setAttribute('disabled', '');
        exportBtn.classList.add('opacity-50', 'cursor-not-allowed');
        exportBtn.setAttribute('title', 'Already in export queue');
      } else {
        exportBtn.removeAttribute('disabled');
        exportBtn.classList.remove('opacity-50', 'cursor-not-allowed');
        exportBtn.setAttribute('title', 'Add to export queue');
      }
    }
    
    if (exportSelectedBtn) {
      const selectedIds = Array.from(state.selectedPhotoIds || []);
      
      if (selectedIds.length === 0) {
        // No photos selected
        exportSelectedBtn.setAttribute('title', 'Select photos to export');
      } else {
        const allInQueue = selectedIds.every(id => this.isPhotoInQueue(id));
        
        if (allInQueue) {
          exportSelectedBtn.setAttribute('title', 'All selected photos already in export queue');
        } else {
          exportSelectedBtn.setAttribute('title', 'Add selected photos to export queue');
        }
      }
    }
  }

  /**
   * Check if a photo is already in the export queue
   */
  isPhotoInQueue(photoId) {
    const state = this.editorState.get();
    const queueItems = state.exportQueueItems || [];
    return queueItems.some(item => item.photo_id === photoId && item.status !== 'completed');
  }

  /**
   * Create queue item element
   */
  createQueueItemElement(item) {
    const div = document.createElement('div');
    div.className = 'bg-gray-800 rounded-lg p-4 flex items-center space-x-4';
    div.dataset.itemId = item.id;
    
    // Status colors
    const statusColors = {
      pending: 'text-gray-400',
      rendering: 'text-blue-400',
      completed: 'text-green-400',
      failed: 'text-red-400'
    };
    
    const statusColor = statusColors[item.status] || 'text-gray-400';
    
    // Status icons
    const statusIcons = {
      pending: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>',
      rendering: '<svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>',
      completed: '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"></path></svg>',
      failed: '<svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"></path></svg>'
    };
    
    const statusIcon = statusIcons[item.status] || statusIcons.pending;
    
    // Get export filename based on pattern
    const state = this.editorState.get();
    const pattern = item.filename_pattern || state.exportFilenamePattern || '{filename}_tg';
    const exportFilename = this.applyFilenamePattern(pattern, item.photo);
    
    // Progress for rendering items
    const activeExports = state.activeExports || new Map();
    const exportProgress = activeExports.get(item.photo_id);
    const progress = exportProgress?.progress || 0;
    
    div.innerHTML = `
      <div class="flex-shrink-0">
        <img src="${item.photo.thumb_256_url}" alt="${item.photo.original_filename}" class="w-16 h-16 object-cover rounded">
      </div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center space-x-2 mb-1">
          <span class="text-sm font-medium text-white truncate">${item.photo.original_filename}</span>
          <svg class="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
          <span class="text-sm text-gray-400 truncate">${exportFilename}</span>
        </div>
        <div class="flex items-center space-x-2">
          <div class="${statusColor}">${statusIcon}</div>
          <span class="text-xs ${statusColor} capitalize">${item.status}</span>
          ${item.status === 'rendering' ? `<span class="text-xs text-gray-500">${progress}%</span>` : ''}
          ${item.status === 'failed' && item.error_message ? `<span class="text-xs text-red-400">Error: ${item.error_message}</span>` : ''}
        </div>
        ${item.status === 'rendering' ? `
          <div class="mt-2 w-full bg-gray-700 rounded-full h-1.5">
            <div class="bg-blue-600 h-1.5 rounded-full transition-all duration-300" style="width: ${progress}%"></div>
          </div>
        ` : ''}
      </div>
      <button class="remove-queue-item-btn flex-shrink-0 p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors" data-item-id="${item.id}" title="Remove from queue">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"></path>
        </svg>
      </button>
    `;
    
    return div;
  }

  /**
   * Apply filename pattern to generate export filename
   */
  applyFilenamePattern(pattern, photo) {
    const originalFilename = photo.original_filename || 'photo.jpg';
    const parts = originalFilename.split('.');
    const ext = parts.pop();
    const basename = parts.join('.');
    
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0].replace(/-/g, '');
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '');
    
    let filename = pattern
      .replace('{filename}', basename)
      .replace('{date}', dateStr)
      .replace('{time}', timeStr);
    
    // Ensure we have extension
    if (!filename.endsWith(`.${ext}`)) {
      filename += `.${ext}`;
    }
    
    return filename;
  }

  /**
   * Update export progress section
   */
  updateExportProgress() {
    const state = this.editorState.get();
    const queueItems = state.exportQueueItems || [];
    const completed = queueItems.filter(i => i.status === 'completed').length;
    const total = queueItems.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    // Update overall progress
    const progressText = document.getElementById('export-overall-progress-text');
    const progressBar = document.getElementById('export-overall-progress-bar');
    
    if (progressText) {
      progressText.textContent = `${completed} / ${total} photos`;
    }
    
    if (progressBar) {
      progressBar.style.width = `${percentage}%`;
    }
    
    // Show/hide progress section
    const progressSection = document.getElementById('export-progress-section');
    if (progressSection) {
      if (state.isExporting) {
        progressSection.classList.remove('hidden');
      } else {
        progressSection.classList.add('hidden');
      }
    }
    
    // Show/hide buttons
    const startBtn = document.getElementById('start-export-btn');
    const cancelBtn = document.getElementById('cancel-export-btn');
    
    if (state.isExporting) {
      startBtn?.classList.add('hidden');
      cancelBtn?.classList.remove('hidden');
    } else {
      startBtn?.classList.remove('hidden');
      cancelBtn?.classList.add('hidden');
    }
    
    // Update current rendering photos
    const currentPhotosDiv = document.getElementById('export-current-photos');
    if (currentPhotosDiv) {
      const rendering = queueItems.filter(i => i.status === 'rendering');
      currentPhotosDiv.innerHTML = '';
      
      rendering.forEach(item => {
        const activeExports = state.activeExports || new Map();
        const exportProgress = activeExports.get(item.photo_id);
        const progress = exportProgress?.progress || 0;
        
        const div = document.createElement('div');
        div.className = 'flex items-center space-x-3 text-sm';
        div.innerHTML = `
          <svg class="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-gray-300 truncate flex-1">${item.photo.original_filename}</span>
          <span class="text-gray-400">${progress}%</span>
        `;
        currentPhotosDiv.appendChild(div);
      });
    }
  }

  /**
   * Update filename pattern preview
   */
  updateFilenamePatternPreview() {
    const state = this.editorState.get();
    const pattern = state.exportFilenamePattern || '{filename}_tg';
    const previewDiv = document.getElementById('filename-preview');
    const warningDiv = document.getElementById('filename-warning');
    
    if (!previewDiv) return;
    
    // Generate previews
    const examples = [
      { original_filename: 'photo.jpg' },
      { original_filename: 'image.png' }
    ];
    
    const previews = examples.map(ex => this.applyFilenamePattern(pattern, ex));
    const previewText = previews.join(', ');
    
    previewDiv.innerHTML = `Preview: <span class="text-white font-mono">${previewText}</span>`;
    
    // Check if pattern would overwrite originals
    const saveLocation = state.exportSaveLocation;
    if (saveLocation === 'original' && pattern === '{filename}') {
      warningDiv?.classList.remove('hidden');
    } else {
      warningDiv?.classList.add('hidden');
    }
  }
}


